import React from 'react';
import { Link } from 'react-router-dom';
import LegalPageLayout, { LegalSection } from '../legal/LegalPageLayout';

const TermsOfService = () => (
    <LegalPageLayout title="Terms of Service" effectiveDate="August 30, 2026" path="/terms">
    <LegalSection heading="1. Agreement to Terms">
      <p>
        These Terms of Service ("Terms") are a binding agreement between you and WebForms.biz
        ("WebForms", "we", "us", or "our") governing your access to and use of the WebForms
        website, form-building platform, APIs, and related services (collectively, the
        "Service"). By creating an account, accessing, or using the Service, you agree to be
        bound by these Terms and our <Link to="/privacy" className="text-primary hover:text-primary/80 font-medium">Privacy Policy</Link>.
        If you do not agree, do not use the Service.
      </p>
      <p>
        If you use the Service on behalf of an organization, you represent that you have
        authority to bind that organization, and "you" refers to that organization.
      </p>
    </LegalSection>

    <LegalSection heading="2. Eligibility and Accounts">
      <p>
        You must be at least 16 years old (or the age of digital consent in your jurisdiction)
        to use the Service. You are responsible for maintaining the confidentiality of your
        account credentials and for all activity that occurs under your account. Notify us
        promptly of any unauthorized use of your account.
      </p>
      <p>
        You must provide accurate and complete registration information and keep it up to date.
      </p>
    </LegalSection>

    <LegalSection heading="3. The Service">
      <p>
        WebForms lets you build, publish, and embed online forms; collect and manage
        submissions; and connect forms to third-party tools. We may add, change, or remove
        features from time to time. We will use reasonable efforts to give notice of material
        changes that adversely affect paid plans.
      </p>
    </LegalSection>

    <LegalSection heading="4. Plans, Billing, and Trials">
      <p>
        Paid plans are billed in advance on a monthly or annual subscription basis through our payment
        processor (Stripe). Fees are stated in U.S. dollars and are exclusive of taxes unless
        stated otherwise. Your subscription renews automatically each billing period until you
        cancel.
      </p>
      <p>
        All paid plans include a 14-day free trial with no credit card required. Unless you
        choose a paid subscription before the trial ends, the trial expires without a charge.
      </p>
      <p id="refund" className="scroll-mt-8">
        <strong className="text-foreground">30-day money-back guarantee.</strong>{' '}
        If you are not satisfied with your first paid subscription purchase, contact us within
        30 days of that purchase to request a full refund. The guarantee applies to the initial
        purchase of a paid plan and does not apply to renewal charges or previously refunded
        subscriptions, except where applicable law requires otherwise.
      </p>
      <p>
        You may cancel at any time from your account settings; cancellation takes effect at the
        end of the current billing period. Fees outside the 30-day money-back guarantee are
        non-refundable except where required by law. If you exceed the limits of your plan
        (such as forms, submissions, or seats), we may ask you to upgrade or restrict overage usage.
      </p>
      <p>
        We may change our prices with at least 30 days' notice; changes apply from your next
        billing period.
      </p>
    </LegalSection>

    <LegalSection heading="5. Your Content and Form Data">
      <p>
        "Customer Content" means the forms you build, the content you place in them, and the
        submissions your respondents provide through them. You retain all rights to Customer
        Content. You grant us a limited, non-exclusive license to host, store, process,
        transmit, and display Customer Content solely to provide and support the Service.
      </p>
      <p>
        You are the data controller of respondent data collected through your forms; WebForms
        processes it on your behalf as a service provider/processor. You are responsible for
        (a) having a lawful basis for collecting respondent data, (b) providing any required
        notices and obtaining any required consents from respondents, and (c) not collecting
        data categories that require safeguards the Service does not provide. Do not use the
        Service to collect regulated health information unless your plan and a signed agreement
        with us expressly permit it.
      </p>
    </LegalSection>

    <LegalSection heading="6. Acceptable Use">
      <p>You agree not to use the Service to:</p>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>violate any law or the rights of others;</li>
        <li>send spam or run phishing, deceptive, or fraudulent forms (including forms that impersonate login pages or other brands);</li>
        <li>collect payment card numbers, government identifiers, or credentials in plain form fields;</li>
        <li>upload malware or attempt to probe, breach, or disrupt the Service or its users;</li>
        <li>scrape, resell, or sublicense the Service except as expressly permitted (e.g., white-label features included in your plan);</li>
        <li>circumvent plan limits, rate limits, or security measures.</li>
      </ul>
      <p>
        We may suspend or remove forms or accounts that we reasonably believe violate these
        Terms or create risk for us, other customers, or respondents.
      </p>
    </LegalSection>

    <LegalSection heading="7. White-Label and Agency Use">
      <p>
        If your plan includes white-label features, you may present forms and portals under
        your own branding to your clients. You remain fully responsible to us for your clients'
        use of the Service, and you must pass through obligations to your clients that are no
        less protective than these Terms.
      </p>
    </LegalSection>

    <LegalSection heading="8. Third-Party Services">
      <p>
        The Service integrates with third-party services (such as payment processors, email
        providers, and other third-party tools). Your use of a third-party service is governed by that
        provider's terms, and we are not responsible for third-party services.
      </p>
    </LegalSection>

    <LegalSection heading="9. Intellectual Property">
      <p>
        The Service, including its software, design, and documentation, is owned by WebForms
        and its licensors and is protected by intellectual-property laws. We grant you a
        limited, non-exclusive, non-transferable right to use the Service in accordance with
        these Terms. You may not copy, modify, or create derivative works of the Service except
        as permitted by law. Feedback you provide may be used by us without obligation to you.
      </p>
    </LegalSection>

    <LegalSection heading="10. Confidentiality and Security">
      <p>
        We maintain administrative, technical, and organizational safeguards designed to
        protect Customer Content, including encryption in transit. No method of transmission or
        storage is completely secure, and you are responsible for configuring your forms and
        account access appropriately.
      </p>
    </LegalSection>

    <LegalSection heading="11. Termination">
      <p>
        You may stop using the Service and delete your account at any time. We may suspend or
        terminate your access if you materially breach these Terms, fail to pay fees when due,
        or if required by law. Upon termination we will make Customer Content available for
        export for a period of 30 days, after which it may be deleted, except where retention is
        required by law.
      </p>
    </LegalSection>

    <LegalSection heading="12. Disclaimers">
      <p>
        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE." TO THE MAXIMUM EXTENT PERMITTED BY
        LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS
        FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL
        BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
      </p>
    </LegalSection>

    <LegalSection heading="13. Limitation of Liability">
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEITHER PARTY WILL BE LIABLE FOR INDIRECT,
        INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS, REVENUE,
        OR DATA, EVEN IF ADVISED OF THE POSSIBILITY. OUR AGGREGATE LIABILITY ARISING OUT OF OR
        RELATING TO THE SERVICE WILL NOT EXCEED THE AMOUNTS YOU PAID US IN THE 12 MONTHS BEFORE
        THE EVENT GIVING RISE TO LIABILITY (OR US $100 IF YOU USE A FREE PLAN). SOME
        JURISDICTIONS DO NOT ALLOW CERTAIN LIMITATIONS, SO SOME OF THE ABOVE MAY NOT APPLY TO
        YOU.
      </p>
    </LegalSection>

    <LegalSection heading="14. Indemnification">
      <p>
        You will indemnify and hold us harmless from claims arising out of your Customer
        Content, your use of the Service in violation of these Terms, or your violation of law
        or third-party rights.
      </p>
    </LegalSection>

    <LegalSection heading="15. Changes to These Terms">
      <p>
        We may update these Terms from time to time. If a change is material, we will provide
        reasonable notice (for example, by email or an in-app notice) before it takes effect.
        Continued use of the Service after the effective date constitutes acceptance of the
        updated Terms.
      </p>
    </LegalSection>

    <LegalSection heading="16. Governing Law and Disputes">
      <p>
        These Terms are governed by the laws of the State of Delaware, USA, without regard to
        conflict-of-law rules. The parties will attempt in good faith to resolve disputes
        informally; failing that, disputes will be resolved in the state or federal courts
        located in Delaware, and each party consents to their jurisdiction.
      </p>
    </LegalSection>

    <LegalSection heading="17. Contact">
      <p>
        Questions about these Terms? Contact us at{' '}
        <a href="mailto:legal@webforms.biz" className="text-primary hover:text-primary/80 font-medium">legal@webforms.biz</a>.
      </p>
    </LegalSection>
  </LegalPageLayout>
);

export default TermsOfService;
